import { useEffect, useState } from "react";
import { apiClient } from "../api/client.js";
import type { MyCredentialsResponse } from "../api/types.js";
import { SectionHeader } from "./help-button.js";

// E1.3.c (ADR-016 V1++) — käyttäjä näkee omat pilvitunnuksensa.
// Read-only: provisiointi + rotation on admin-toiminto. Käyttäjälle
// näytetään hetznerKeyName + s3AccessKeyId jotta hän voi pyytää
// rotation:ia oikealla nimellä.

export function MyCredentialsSection() {
  const [data, setData] = useState<MyCredentialsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.getMyCredentials();
        setData(r);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section className="rounded-lg border border-slate-700 bg-slate-900 p-6">
      <SectionHeader
        title="Pilvitunnukset"
        help={
          "Kratti varmuuskopioi tiedostosi Hetzner Object Storage -palveluun " +
          "(Helsinki). Jokaisella käyttäjällä on omat S3-tunnukset jotka admin " +
          "provisioi. Salasana säilyy vain tietokoneellasi (taustaohjelman " +
          "salatussa konfiguraatiossa)."
        }
      />

      {loading && <p className="mt-3 text-sm text-slate-400">Ladataan…</p>}
      {error && (
        <p className="mt-3 rounded border border-red-700 bg-red-950/40 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      {data && !data.configured && (
        <p className="mt-3 text-sm text-slate-400">
          Per-user-tunnuksia ei ole konfiguroitu palvelimella. Käytössä on
          jaettu V1-malli — kaikki näkevät saman pilvitunnuksen.
        </p>
      )}

      {data && data.configured && !data.provisioned && (
        <p className="mt-3 rounded border border-amber-700 bg-amber-950/40 px-3 py-2 text-sm text-amber-200">
          {data.message}
        </p>
      )}

      {data && data.configured && data.provisioned && (
        <dl className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Row
            label="Pilvitallennus"
            value={
              data.scalewayBucketName
                ? `Scaleway (${data.scalewayRegion ?? "?"})`
                : "Hetzner Object Storage"
            }
            hint={
              data.scalewayBucketName
                ? "Per-käyttäjä-projekti ja -bucket — eristyksessä muista käyttäjistä."
                : "Vanha jaettu malli. Pyydä admin:lta Scaleway-provisiointi."
            }
          />
          <Row
            label={data.scalewayBucketName ? "Bucket" : "Avaimen nimi (Hetzner)"}
            value={data.scalewayBucketName ?? data.hetznerKeyName ?? "—"}
            mono
          />
          <Row label="Access Key ID" value={data.s3AccessKeyId} mono />
          {data.scalewayProjectId && (
            <Row
              label="Projekti-ID (Scaleway)"
              value={data.scalewayProjectId}
              mono
              hint="Käytetään admin-tukipyynnöissä jos data-pääsy pitää selvittää."
            />
          )}
          <Row
            label="Provisioitu"
            value={new Date(
              data.provisionedAt ?? data.createdAt
            ).toLocaleString("fi-FI")}
          />
          <Row
            label="Viimeksi rotoitu"
            value={
              data.rotatedAt
                ? new Date(data.rotatedAt).toLocaleString("fi-FI")
                : "—"
            }
          />
          <Row
            label="Repo-salasana"
            value={data.hasPassphrase ? "Tallennettu" : "Puuttuu"}
            valueClass={
              data.hasPassphrase ? "text-emerald-300" : "text-red-300"
            }
            hint="Taustaohjelma käyttää tätä Kopia-arkiston salaukseen. Et näe arvoa, vain status."
          />
        </dl>
      )}

      <p className="mt-4 text-xs text-slate-500">
        Tarvitsetko avaimen rotation:ia? Ota yhteyttä ylläpitäjään ja kerro
        avaimen tai bucket:n nimi tämän näkymän mukaan.
      </p>
    </section>
  );
}

function Row(props: {
  label: string;
  value: string;
  mono?: boolean;
  hint?: string;
  valueClass?: string;
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-400">{props.label}</dt>
      <dd
        className={`mt-1 text-sm ${props.valueClass ?? "text-slate-100"} ${
          props.mono ? "font-mono" : ""
        }`}
      >
        {props.value}
      </dd>
      {props.hint && (
        <p className="mt-1 text-xs text-slate-500">{props.hint}</p>
      )}
    </div>
  );
}
